"use client"

import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { Page, PageBody, PageHeader } from '@open-mercato/ui/backend/Page'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { useAppEvent } from '@open-mercato/ui/backend/injection/useAppEvent'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@open-mercato/ui/primitives/card'
import { Input } from '@open-mercato/ui/primitives/input'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { Button } from '@open-mercato/ui/primitives/button'
import { Separator } from '@open-mercato/ui/primitives/separator'

type LiveItem = {
  id: string
  vehicleId: string
  driverId: string | null
  dispatchId: string | null
  legId: string | null
  latitude: number
  longitude: number
  speedKmh: number | null
  headingDeg: number | null
  source: string
  recordedAt: string
  registrationPlate: string | null
  gpsProvider: string | null
  driverName: string | null
  freshness: 'fresh' | 'stale' | 'offline'
  ageMinutes: number
  trail: Array<{ latitude: number; longitude: number; recordedAt: string }>
}

type LiveResponse = {
  items: LiveItem[]
  geofenceLocations: Array<{
    locationId: string
    name: string
    latitude: number
    longitude: number
    geofenceRadiusM: number
  }>
  generatedAt: string
}

type HistoryResponse = {
  vehicleId: string
  registrationPlate: string
  points: Array<{
    id: string
    latitude: number
    longitude: number
    speedKmh: number | null
    headingDeg: number | null
    recordedAt: string
  }>
  stops: Array<{
    startedAt: string
    endedAt: string
    durationMinutes: number
    latitude: number
    longitude: number
  }>
  summary: {
    pointCount: number
    distanceKmEstimate: number
  }
}

function freshnessVariant(value: LiveItem['freshness']): 'default' | 'secondary' | 'destructive' {
  if (value === 'fresh') return 'default'
  if (value === 'stale') return 'secondary'
  return 'destructive'
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

function formatCoordinate(value: number): string {
  return value.toFixed(4)
}

function formatSpeed(value: number | null | undefined): string {
  if (value == null) return '—'
  return `${Math.round(value)} km/h`
}

function projectPoint(
  latitude: number,
  longitude: number,
  bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number },
  width: number,
  height: number,
) {
  const padding = 40
  const safeWidth = Math.max(1, width - padding * 2)
  const safeHeight = Math.max(1, height - padding * 2)
  const lngSpan = Math.max(0.01, bounds.maxLng - bounds.minLng)
  const latSpan = Math.max(0.01, bounds.maxLat - bounds.minLat)
  const x = padding + ((longitude - bounds.minLng) / lngSpan) * safeWidth
  const y = padding + (1 - (latitude - bounds.minLat) / latSpan) * safeHeight
  return { x, y }
}

function buildBounds(data: LiveResponse | null, history: HistoryResponse | null) {
  const latitudes: number[] = []
  const longitudes: number[] = []
  for (const item of data?.items ?? []) {
    latitudes.push(item.latitude)
    longitudes.push(item.longitude)
    for (const point of item.trail) {
      latitudes.push(point.latitude)
      longitudes.push(point.longitude)
    }
  }
  for (const location of data?.geofenceLocations ?? []) {
    latitudes.push(location.latitude)
    longitudes.push(location.longitude)
  }
  for (const point of history?.points ?? []) {
    latitudes.push(point.latitude)
    longitudes.push(point.longitude)
  }
  if (!latitudes.length || !longitudes.length) {
    return { minLat: 49, maxLat: 55, minLng: 14, maxLng: 24 }
  }
  return {
    minLat: Math.min(...latitudes) - 0.1,
    maxLat: Math.max(...latitudes) + 0.1,
    minLng: Math.min(...longitudes) - 0.1,
    maxLng: Math.max(...longitudes) + 0.1,
  }
}

function geofenceRadiusPx(
  radiusM: number,
  latitude: number,
  bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number },
  width: number,
  height: number,
) {
  const metersPerLatDegree = 111_320
  const metersPerLngDegree = Math.cos((latitude * Math.PI) / 180) * 111_320
  const latSpanMeters = Math.max(1, (bounds.maxLat - bounds.minLat) * metersPerLatDegree)
  const lngSpanMeters = Math.max(1, (bounds.maxLng - bounds.minLng) * metersPerLngDegree)
  const pxPerMeterX = width / lngSpanMeters
  const pxPerMeterY = height / latSpanMeters
  return Math.max(8, Math.min(60, radiusM * Math.min(pxPerMeterX, pxPerMeterY)))
}

async function fetchLiveTracking(): Promise<LiveResponse> {
  return readApiResultOrThrow<LiveResponse>('/api/road/tracking/live?includeTrailMinutes=30', undefined, {
    errorMessage: 'Failed to fetch live road tracking.',
  })
}

async function fetchHistory(vehicleId: string, fromHours: number): Promise<HistoryResponse> {
  const to = new Date()
  const from = new Date(to.getTime() - fromHours * 60 * 60 * 1000)
  return readApiResultOrThrow<HistoryResponse>(
    `/api/road/tracking/vehicle/${encodeURIComponent(vehicleId)}/history?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}&maxPoints=400`,
    undefined,
    { errorMessage: 'Failed to fetch vehicle history.' },
  )
}

export default function RoadTrackingPage() {
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()
  const queryClient = useQueryClient()
  const [search, setSearch] = React.useState('')
  const deferredSearch = useDeferredValue(search)
  const [selectedVehicleId, setSelectedVehicleId] = React.useState<string | null>(null)
  const [historyRangeHours, setHistoryRangeHours] = React.useState(6)
  const [replayIndex, setReplayIndex] = React.useState(0)

  const liveQuery = useQuery({
    queryKey: ['road_tracking', 'live', scopeVersion],
    queryFn: fetchLiveTracking,
    refetchInterval: 15000,
  })

  const selectedVehicle = React.useMemo(() => {
    const items = liveQuery.data?.items ?? []
    if (!items.length) return null
    return items.find((item) => item.vehicleId === selectedVehicleId) ?? items[0] ?? null
  }, [liveQuery.data?.items, selectedVehicleId])

  React.useEffect(() => {
    if (!selectedVehicleId && liveQuery.data?.items?.[0]?.vehicleId) {
      setSelectedVehicleId(liveQuery.data.items[0].vehicleId)
    }
  }, [liveQuery.data?.items, selectedVehicleId])

  const historyQuery = useQuery({
    queryKey: ['road_tracking', 'history', scopeVersion, selectedVehicle?.vehicleId ?? null, historyRangeHours],
    queryFn: () => fetchHistory(selectedVehicle!.vehicleId, historyRangeHours),
    enabled: Boolean(selectedVehicle?.vehicleId),
  })

  React.useEffect(() => {
    setReplayIndex(0)
  }, [historyQuery.data?.vehicleId, historyRangeHours])

  useAppEvent('road.tracking.position.updated', () => {
    void queryClient.invalidateQueries({ queryKey: ['road_tracking'] })
  }, [queryClient])

  const filteredItems = React.useMemo(() => {
    const items = liveQuery.data?.items ?? []
    const query = deferredSearch.trim().toLowerCase()
    if (!query) return items
    return items.filter((item) => {
      const haystack = [item.registrationPlate, item.driverName, item.gpsProvider].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(query)
    })
  }, [deferredSearch, liveQuery.data?.items])

  const mapBounds = React.useMemo(
    () => buildBounds(liveQuery.data ?? null, historyQuery.data ?? null),
    [historyQuery.data, liveQuery.data],
  )

  const replayPoint = historyQuery.data?.points?.[replayIndex] ?? null

  if (liveQuery.isLoading && !liveQuery.data) {
    return (
      <Page>
        <PageHeader
          title={t('road_tracking.pages.title', 'Road Tracking')}
          description={t('road_tracking.pages.description', 'Live vehicle positions and replay history for road execution.')}
        />
        <PageBody>
          <LoadingMessage label={t('road_tracking.messages.loading', 'Loading road tracking…')} />
        </PageBody>
      </Page>
    )
  }

  if (liveQuery.isError || !liveQuery.data) {
    return (
      <Page>
        <PageHeader
          title={t('road_tracking.pages.title', 'Road Tracking')}
          description={t('road_tracking.pages.description', 'Live vehicle positions and replay history for road execution.')}
        />
        <PageBody>
          <ErrorMessage
            label={t('road_tracking.messages.loadErrorTitle', 'Could not load road tracking')}
            description={t('road_tracking.messages.loadError', 'Try again in a moment or refresh the page.')}
          />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={t('road_tracking.pages.title', 'Road Tracking')}
        description={t('road_tracking.pages.description', 'Live vehicle positions and replay history for road execution.')}
      />
      <PageBody className="space-y-4">
        <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
          <Card className="min-h-[720px]">
            <CardHeader>
              <CardTitle>{t('road_tracking.sidebar.title', 'Vehicles')}</CardTitle>
              <CardDescription>{t('road_tracking.sidebar.description', 'Freshness, provider, and live execution context.')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('road_tracking.sidebar.searchPlaceholder', 'Search by plate or driver')}
              />
              <div className="grid grid-cols-3 gap-2 text-xs">
                <Badge variant="default" className="justify-center">{liveQuery.data.items.filter((item) => item.freshness === 'fresh').length} {t('road_tracking.sidebar.fresh', 'fresh')}</Badge>
                <Badge variant="secondary" className="justify-center">{liveQuery.data.items.filter((item) => item.freshness === 'stale').length} {t('road_tracking.sidebar.stale', 'stale')}</Badge>
                <Badge variant="destructive" className="justify-center">{liveQuery.data.items.filter((item) => item.freshness === 'offline').length} {t('road_tracking.sidebar.offline', 'offline')}</Badge>
              </div>
              <Separator />
              <div className="space-y-2">
                {filteredItems.map((item) => (
                  <Button
                    key={item.id}
                    type="button"
                    variant="ghost"
                    className={`h-auto w-full rounded-lg border p-3 text-left transition ${
                      selectedVehicle?.vehicleId === item.vehicleId
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40'
                    }`}
                    onClick={() => setSelectedVehicleId(item.vehicleId)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium">{item.registrationPlate ?? item.vehicleId}</div>
                      <Badge variant={freshnessVariant(item.freshness)}>{t(`road_tracking.freshness.${item.freshness}`, item.freshness)}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {item.driverName ?? t('road_tracking.sidebar.noDriver', 'No driver')} • {item.gpsProvider ?? t('road_tracking.sidebar.noProvider', 'No provider')}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{formatSpeed(item.speedKmh)}</span>
                      <span>{t('road_tracking.sidebar.updatedAgo', '{{minutes}} min ago').replace('{{minutes}}', String(item.ageMinutes))}</span>
                    </div>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('road_tracking.map.title', 'Live Tracking Map')}</CardTitle>
                <CardDescription>{t('road_tracking.map.description', 'Custom SVG live map with trails, geofence overlays, and history replay.')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-hidden rounded-xl border bg-[linear-gradient(180deg,#f7fafc_0%,#eef4f8_100%)]">
                  <svg viewBox="0 0 1100 560" className="h-[560px] w-full">
                    <defs>
                      <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                        <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(148,163,184,0.22)" strokeWidth="1" />
                      </pattern>
                    </defs>
                    <rect width="1100" height="560" fill="url(#grid)" />

                    {liveQuery.data.geofenceLocations.map((location) => {
                      const point = projectPoint(location.latitude, location.longitude, mapBounds, 1100, 560)
                      const radius = geofenceRadiusPx(location.geofenceRadiusM, location.latitude, mapBounds, 1100, 560)
                      return (
                        <g key={location.locationId}>
                          <circle cx={point.x} cy={point.y} r={radius} fill="rgba(14,165,233,0.08)" stroke="rgba(14,165,233,0.45)" strokeDasharray="5 4" />
                          <text x={point.x + radius + 6} y={point.y - 6} fontSize="11" fill="#0f172a">{location.name}</text>
                        </g>
                      )
                    })}

                    {liveQuery.data.items.map((item) => {
                      if (item.trail.length < 2) return null
                      const trailPath = item.trail
                        .map((point, index) => {
                          const projected = projectPoint(point.latitude, point.longitude, mapBounds, 1100, 560)
                          return `${index === 0 ? 'M' : 'L'} ${projected.x} ${projected.y}`
                        })
                        .join(' ')
                      return (
                        <path
                          key={`${item.id}-trail`}
                          d={trailPath}
                          fill="none"
                          stroke={selectedVehicle?.vehicleId === item.vehicleId ? '#0f766e' : 'rgba(15,118,110,0.35)'}
                          strokeWidth={selectedVehicle?.vehicleId === item.vehicleId ? 4 : 2}
                        />
                      )
                    })}

                    {historyQuery.data?.points && historyQuery.data.points.length > 1 ? (
                      <path
                        d={historyQuery.data.points
                          .map((point, index) => {
                            const projected = projectPoint(point.latitude, point.longitude, mapBounds, 1100, 560)
                            return `${index === 0 ? 'M' : 'L'} ${projected.x} ${projected.y}`
                          })
                          .join(' ')}
                        fill="none"
                        stroke="#f97316"
                        strokeWidth={3}
                        strokeDasharray="8 6"
                      />
                    ) : null}

                    {liveQuery.data.items.map((item) => {
                      const point = projectPoint(item.latitude, item.longitude, mapBounds, 1100, 560)
                      const heading = ((item.headingDeg ?? 0) - 90) * (Math.PI / 180)
                      const arrowX = point.x + Math.cos(heading) * 18
                      const arrowY = point.y + Math.sin(heading) * 18
                      const isSelected = selectedVehicle?.vehicleId === item.vehicleId
                      return (
                        <g key={item.id}>
                          <circle cx={point.x} cy={point.y} r={isSelected ? 10 : 8} fill={item.freshness === 'fresh' ? '#15803d' : item.freshness === 'stale' ? '#ca8a04' : '#dc2626'} />
                          <line x1={point.x} y1={point.y} x2={arrowX} y2={arrowY} stroke="#0f172a" strokeWidth={2} />
                          <text x={point.x + 14} y={point.y - 12} fontSize="12" fontWeight="600" fill="#0f172a">
                            {item.registrationPlate ?? item.vehicleId}
                          </text>
                          <text x={point.x + 14} y={point.y + 4} fontSize="11" fill="#334155">
                            {formatSpeed(item.speedKmh)}
                          </text>
                        </g>
                      )
                    })}

                    {replayPoint ? (() => {
                      const point = projectPoint(replayPoint.latitude, replayPoint.longitude, mapBounds, 1100, 560)
                      return <circle cx={point.x} cy={point.y} r={9} fill="#f97316" stroke="#fff" strokeWidth={3} />
                    })() : null}
                  </svg>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <Card>
                <CardHeader>
                  <CardTitle>{t('road_tracking.history.title', 'History Replay')}</CardTitle>
                  <CardDescription>{selectedVehicle?.registrationPlate ?? t('road_tracking.history.noVehicle', 'Select a vehicle to inspect history.')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {[1, 6, 24].map((hours) => (
                      <Button
                        key={hours}
                        type="button"
                        variant={historyRangeHours === hours ? 'default' : 'outline'}
                        onClick={() => setHistoryRangeHours(hours)}
                      >
                        {t('road_tracking.history.rangeHours', '{{hours}}h').replace('{{hours}}', String(hours))}
                      </Button>
                    ))}
                  </div>
                  {historyQuery.isLoading ? <LoadingMessage label={t('road_tracking.history.loading', 'Loading history…')} /> : null}
                  {historyQuery.isError ? (
                    <ErrorMessage
                      label={t('road_tracking.history.loadErrorTitle', 'Could not load history')}
                      description={t('road_tracking.history.loadError', 'Try another range or refresh the page.')}
                    />
                  ) : null}
                  {historyQuery.data ? (
                    <div className="space-y-4">
                      <div className="grid gap-3 md:grid-cols-3">
                        <div className="rounded-lg border p-3">
                          <div className="text-xs text-muted-foreground">{t('road_tracking.history.summary.points', 'Points')}</div>
                          <div className="text-xl font-semibold">{historyQuery.data.summary.pointCount}</div>
                        </div>
                        <div className="rounded-lg border p-3">
                          <div className="text-xs text-muted-foreground">{t('road_tracking.history.summary.distance', 'Distance')}</div>
                          <div className="text-xl font-semibold">{historyQuery.data.summary.distanceKmEstimate} km</div>
                        </div>
                        <div className="rounded-lg border p-3">
                          <div className="text-xs text-muted-foreground">{t('road_tracking.history.summary.stops', 'Stops')}</div>
                          <div className="text-xl font-semibold">{historyQuery.data.stops.length}</div>
                        </div>
                      </div>
                      {historyQuery.data.points.length > 0 ? (
                        <div className="space-y-2">
                          <input
                            className="w-full"
                            type="range"
                            min={0}
                            max={Math.max(0, historyQuery.data.points.length - 1)}
                            step={1}
                            value={replayIndex}
                            onChange={(event) => setReplayIndex(Number(event.target.value))}
                          />
                          <div className="rounded-lg border p-3 text-sm">
                            <div className="font-medium">{t('road_tracking.history.replayPoint', 'Replay point')}</div>
                            <div className="mt-2 text-muted-foreground">
                              {replayPoint ? `${formatDateTime(replayPoint.recordedAt)} • ${formatCoordinate(replayPoint.latitude)}, ${formatCoordinate(replayPoint.longitude)} • ${formatSpeed(replayPoint.speedKmh)}` : '—'}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <div className="space-y-2">
                        {historyQuery.data.stops.length > 0 ? historyQuery.data.stops.map((stop) => (
                          <div key={`${stop.startedAt}-${stop.endedAt}`} className="rounded-lg border p-3 text-sm">
                            <div className="font-medium">{t('road_tracking.history.stopTitle', 'Detected stop')}</div>
                            <div className="text-muted-foreground">
                              {formatDateTime(stop.startedAt)} - {formatDateTime(stop.endedAt)}
                            </div>
                            <div className="text-muted-foreground">
                              {t('road_tracking.history.stopDuration', '{{minutes}} min').replace('{{minutes}}', String(stop.durationMinutes))} • {formatCoordinate(stop.latitude)}, {formatCoordinate(stop.longitude)}
                            </div>
                          </div>
                        )) : (
                          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                            {t('road_tracking.history.noStops', 'No stops longer than 15 minutes were detected in the selected range.')}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('road_tracking.detail.title', 'Selected Vehicle')}</CardTitle>
                  <CardDescription>{t('road_tracking.detail.description', 'Execution context, freshness, and latest tracked coordinates.')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedVehicle ? (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-xl font-semibold">{selectedVehicle.registrationPlate ?? selectedVehicle.vehicleId}</div>
                          <div className="text-sm text-muted-foreground">{selectedVehicle.driverName ?? t('road_tracking.detail.noDriver', 'No driver assigned')}</div>
                        </div>
                        <Badge variant={freshnessVariant(selectedVehicle.freshness)}>
                          {t(`road_tracking.freshness.${selectedVehicle.freshness}`, selectedVehicle.freshness)}
                        </Badge>
                      </div>
                      <Separator />
                      <dl className="grid gap-3 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-muted-foreground">{t('road_tracking.detail.fields.provider', 'Provider')}</dt>
                          <dd>{selectedVehicle.gpsProvider ?? '—'}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-muted-foreground">{t('road_tracking.detail.fields.source', 'Source')}</dt>
                          <dd>{selectedVehicle.source}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-muted-foreground">{t('road_tracking.detail.fields.speed', 'Speed')}</dt>
                          <dd>{formatSpeed(selectedVehicle.speedKmh)}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-muted-foreground">{t('road_tracking.detail.fields.heading', 'Heading')}</dt>
                          <dd>{selectedVehicle.headingDeg == null ? '—' : `${Math.round(selectedVehicle.headingDeg)}°`}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-muted-foreground">{t('road_tracking.detail.fields.coordinates', 'Coordinates')}</dt>
                          <dd>{formatCoordinate(selectedVehicle.latitude)}, {formatCoordinate(selectedVehicle.longitude)}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-muted-foreground">{t('road_tracking.detail.fields.recordedAt', 'Recorded at')}</dt>
                          <dd>{formatDateTime(selectedVehicle.recordedAt)}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-muted-foreground">{t('road_tracking.detail.fields.dispatch', 'Dispatch')}</dt>
                          <dd>{selectedVehicle.dispatchId ?? '—'}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-muted-foreground">{t('road_tracking.detail.fields.leg', 'Leg')}</dt>
                          <dd>{selectedVehicle.legId ?? '—'}</dd>
                        </div>
                      </dl>
                    </>
                  ) : (
                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                      {t('road_tracking.detail.empty', 'No vehicle positions are available yet.')}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </PageBody>
    </Page>
  )
}
